import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import MobileStickyBar from '../components/common/MobileStickyBar';
import ContactModal from '../components/common/ContactModal';
import {
  Check, ChevronDown, ArrowRight, Star, Users, Wallet,
  Calendar, Music, Camera, Utensils, Lightbulb, MapPin,
  Clock, AlertTriangle, MessageCircle, ExternalLink, Plus, Minus
} from 'lucide-react';

// ── THÈMES 2026 ────────────────────────────────────────────────────────────
const THEMES = [
  { name: 'Dolce Vita', emoji: '🇮🇹', desc: 'Terrasse ensoleillée, citrons, rotin, lin blanc. La douceur de vivre à l\'italienne.', colors: ['#F5E6C8', '#D4AF37', '#2D6A4F'], popular: true },
  { name: 'Guinguette', emoji: '🪔', desc: 'Guirlandes lumineuses, tables rustiques, pétanque, musette et vin rosé. L\'authenticité française.', colors: ['#F9C5D1', '#D4AF37', '#5DBCBF'], popular: true },
  { name: 'Black & Gold', emoji: '✨', desc: 'Élégance absolue, bougies, confettis dorés, nappes noires. Sophistiqué et intemporel.', colors: ['#0B1E20', '#D4AF37', '#FFFFFF'], popular: true },
  { name: 'Disco / Années 80', emoji: '🪩', desc: 'Boule à facettes, sequins, couleurs flashy, musique rétro. La fête garantie !', colors: ['#C026D3', '#F59E0B', '#06B6D4'], popular: false },
  { name: 'Tropical', emoji: '🌴', desc: 'Palmes, fleurs exotiques, couleurs vives, cocktails fruités. Évasion garantie.', colors: ['#10B981', '#F59E0B', '#EC4899'], popular: false },
  { name: 'Garden Party', emoji: '🌸', desc: 'Fleurs fraîches, nappes fleuries, lumière naturelle, repas champêtre. Romantique et élégant.', colors: ['#6EE7B7', '#FDE68A', '#FCA5A5'], popular: true },
  { name: 'Festival', emoji: '🎸', desc: 'Scène live, stands food, lumières colorées, ambiance concert. Créatif et festif.', colors: ['#7C3AED', '#F59E0B', '#EF4444'], popular: false },
  { name: 'Rétro 90s', emoji: '📼', desc: 'Cassettes, couleurs néon, musique pop, jeux d\'époque. Nostalgie et bonne humeur.', colors: ['#EC4899', '#8B5CF6', '#06B6D4'], popular: false },
  { name: 'Bohème', emoji: '🌙', desc: 'Macramé, dreamcatchers, bougies, tissus, nature. Doux et poétique.', colors: ['#D4AF37', '#92400E', '#6B7280'], popular: false },
  { name: 'Lounge / Chic', emoji: '🥂', desc: 'Canapés, bougies, cocktails raffinés, lumière tamisée. L\'élégance décontractée.', colors: ['#1F2937', '#9CA3AF', '#D4AF37'], popular: false },
  { name: 'Chic Parisien', emoji: '🗼', desc: 'Noir, blanc, dorures, fleurs blanches, champagne. L\'élégance à la parisienne.', colors: ['#1F2937', '#FFFFFF', '#D4AF37'], popular: false },
  { name: 'Nature & Forêt', emoji: '🌲', desc: 'Branches, champignons, bougies, pierres, laine. Cocooning et organique.', colors: ['#166534', '#92400E', '#F5F5DC'], popular: false },
];

// ── CHECKLIST ────────────────────────────────────────────────────────────────
const CHECKLIST_CATEGORIES = [
  {
    cat: 'Lieu & logistique', icon: MapPin, color: '#5DBCBF',
    items: [
      { label: 'Choisir et réserver le lieu (salle, jardin, domaine…)', delai: '6 mois avant' },
      { label: 'Vérifier la capacité et l\'accessibilité PMR', delai: '6 mois avant' },
      { label: 'Prévoir le parking invités', delai: '1 mois avant' },
      { label: 'Vérifier les autorisations (bruit, alcool…)', delai: '3 mois avant' },
      { label: 'Prévoir tente ou barnum en cas de pluie', delai: '1 mois avant' },
    ]
  },
  {
    cat: 'Animations & divertissement', icon: Music, color: '#D4AF37',
    items: [
      { label: 'Réserver le DJ ou groupe live', delai: '3 mois avant' },
      { label: 'Installer le photobooth ou borne 360°', delai: 'Jour J matin' },
      { label: 'Prévoir jeux (blind test, pétanque, jeux bois…)', delai: '1 mois avant' },
      { label: 'Organiser un quiz ou animation surprise', delai: '2 semaines avant' },
      { label: 'Prévoir micro sans fil pour discours', delai: '1 semaine avant' },
    ]
  },
  {
    cat: 'Décoration', icon: Star, color: '#F4A7B2',
    items: [
      { label: 'Définir le thème et la palette couleurs', delai: '3 mois avant' },
      { label: 'Réserver arche ballons et décoration', delai: '2 mois avant' },
      { label: 'Prévoir guirlandes lumineuses et bougies', delai: '1 mois avant' },
      { label: 'Commander nappes, housses de chaises', delai: '1 mois avant' },
      { label: 'Créer marque-places et numéros de table', delai: '2 semaines avant' },
      { label: 'Fleurs fraîches (à commander 1 semaine avant)', delai: '1 semaine avant' },
    ]
  },
  {
    cat: 'Food & boissons', icon: Utensils, color: '#1F9D7A',
    items: [
      { label: 'Choisir entre buffet, assis ou cocktail', delai: '3 mois avant' },
      { label: 'Réserver le traiteur ou cuisiner soi-même', delai: '2 mois avant' },
      { label: 'Prévoir bar à bonbons, barbe à papa, popcorn', delai: '1 mois avant' },
      { label: 'Calculer les quantités alcool et soft (1 verre/personne/heure)', delai: '2 semaines avant' },
      { label: 'Prévoir la pièce montée ou gâteau', delai: '1 mois avant' },
      { label: 'Penser aux allergies et régimes alimentaires', delai: '1 mois avant' },
    ]
  },
  {
    cat: 'Invités & invitations', icon: Users, color: '#C58B19',
    items: [
      { label: 'Établir la liste définitive des invités', delai: '3 mois avant' },
      { label: 'Envoyer les save-the-date', delai: '2 mois avant' },
      { label: 'Envoyer les invitations avec lien RSVP', delai: '1 mois avant' },
      { label: 'Faire le point sur les réponses (J-10)', delai: '10 jours avant' },
      { label: 'Relancer les non-répondants', delai: '1 semaine avant' },
      { label: 'Préparer le plan de table', delai: '3 jours avant' },
    ]
  },
  {
    cat: 'Éclairage & ambiance', icon: Lightbulb, color: '#8B5CF6',
    items: [
      { label: 'Prévoir spots LED colorés ou projecteurs', delai: '1 mois avant' },
      { label: 'Guirlandes lumineuses intérieur/extérieur', delai: '1 mois avant' },
      { label: 'Bougies et photophores sur les tables', delai: '1 semaine avant' },
      { label: 'Vérifier les prises électriques disponibles', delai: '1 mois avant' },
    ]
  },
  {
    cat: 'Jour J', icon: Calendar, color: '#EF4444',
    items: [
      { label: 'Préparer le planning heure par heure', delai: '1 semaine avant' },
      { label: 'Déléguer des rôles à des proches', delai: '2 semaines avant' },
      { label: 'Prévoir sac de secours (pansements, trousse…)', delai: 'Jour J matin' },
      { label: 'Charger les téléphones / appareils photo', delai: 'Jour J matin' },
      { label: 'Préparer playlist de secours', delai: '3 jours avant' },
      { label: 'Prévoir le rangement/nettoyage après', delai: 'Jour J soir' },
    ]
  },
];

// ── BUDGET TEMPLATE ──────────────────────────────────────────────────────────
const BUDGET_CATEGORIES = [
  { cat: 'Lieu & logistique', postes: ['Location salle', 'Barnum / tente', 'Parking', 'Transport invités'] },
  { cat: 'Animations', postes: ['DJ / Groupe live', 'Photobooth Show Événement', 'Jeux & animations', 'Sono & éclairage'] },
  { cat: 'Décoration', postes: ['Arche ballons', 'Guirlandes & bougies', 'Nappes & housses', 'Fleurs fraîches'] },
  { cat: 'Food & boissons', postes: ['Traiteur / buffet', 'Gâteau d\'anniversaire', 'Bar à bonbons', 'Alcools & softs'] },
  { cat: 'Invitations & papeterie', postes: ['Invitations', 'Marque-places', 'Photo souvenir'] },
  { cat: 'Tenue & beauté', postes: ['Tenue anniversaire', 'Coiffure & maquillage'] },
  { cat: 'Imprévus', postes: ['Budget sécurité (10%)'] },
];

// ── PRESTATAIRES TYPES ────────────────────────────────────────────────────────
const PRESTATAIRES_TYPES = [
  { role: 'DJ / Animation musicale', icon: '🎧', desc: 'Mix live, animation, ambiance. Réserver 3 à 6 mois avant.', urgent: true },
  { role: 'Photographe', icon: '📸', desc: 'Capturer les moments clés. Notre recommandation : Laurie Gerling.', urgent: true, link: '/prestataires-recommandes' },
  { role: 'Traiteur / Chef', icon: '🍽️', desc: 'Buffet, cocktail ou assis. Devis à demander 2 à 3 mois avant.', urgent: false },
  { role: 'Décorateur', icon: '🎨', desc: 'Ballon artistique, arche, scénographie. Show Événement peut vous aider !', urgent: false, link: '/fabrication' },
  { role: 'Vidéaste', icon: '🎬', desc: 'Clip souvenir, timelapse, aftermovie. Réserver en même temps que le photographe.', urgent: false },
  { role: 'Fleuriste', icon: '💐', desc: 'Compositions fraîches. À contacter 4 semaines avant.', urgent: false },
  { role: 'Maquillage / Coiffure', icon: '💄', desc: 'Prestation à domicile ou en salon. À réserver tôt.', urgent: false },
  { role: 'Food truck', icon: '🚚', desc: 'Option originale et conviviale. Vérifier disponibilités 2 mois avant.', urgent: false },
];

// ── ERREURS À ÉVITER ─────────────────────────────────────────────────────────
const ERREURS = [
  { erreur: 'Sous-estimer le budget', conseil: 'Ajoutez toujours 15% d\'imprévus. Les dépenses surprise arrivent toujours.' },
  { erreur: 'Réserver trop tard', conseil: 'Les bons prestataires (DJ, photographe) sont pris 3 à 6 mois à l\'avance.' },
  { erreur: 'Oublier les restrictions alimentaires', conseil: 'Demandez les allergies et régimes lors des RSVP. Prévoyez toujours des alternatives.' },
  { erreur: 'Négliger l\'éclairage', conseil: 'L\'éclairage transforme une salle ordinaire en décor magique. C\'est souvent sous-estimé.' },
  { erreur: 'Pas de plan B météo', conseil: 'Pour un événement outdoor, prévoyer toujours un barnum ou une solution repli.' },
  { erreur: 'Oublier de déléguer', conseil: 'Nommez 2-3 personnes de confiance pour gérer les questions le Jour J. Profitez de votre fête !' },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl overflow-hidden cursor-pointer" style={{ border: '1px solid var(--border)', background: '#FFFFFF' }}>
      <button className="w-full flex items-center justify-between px-5 py-4 text-left gap-4" onClick={() => setOpen(!open)}>
        <span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{q}</span>
        <ChevronDown size={16} style={{ color: 'var(--text-muted)', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>
      {open && <div className="px-5 pb-4"><p className="text-sm" style={{ color: 'var(--text-muted)', lineHeight: '1.7' }}>{a}</p></div>}
    </div>
  );
}

export default function GuideAnniversaireAdultePage() {
  const [contactOpen, setContactOpen] = useState(false);
  const [checkItems, setCheckItems] = useState(
    CHECKLIST_CATEGORIES.flatMap(c => c.items.map(i => ({ ...i, done: false, cat: c.cat })))
  );
  const [budgetItems, setBudgetItems] = useState(
    BUDGET_CATEGORIES.flatMap(c => c.postes.map(p => ({ cat: c.cat, label: p, prevu: 0, reel: 0, paye: 0 })))
  );
  const [activeTheme, setActiveTheme] = useState(null);

  const doneCount = checkItems.filter(i => i.done).length;
  const progress = Math.round((doneCount / checkItems.length) * 100);
  const totalPrevu = budgetItems.reduce((s, i) => s + (parseFloat(i.prevu) || 0), 0);
  const totalPaye = budgetItems.reduce((s, i) => s + (parseFloat(i.paye) || 0), 0);
  const totalRestant = totalPrevu - totalPaye;

  const toggleCheck = (label) => {
    setCheckItems(prev => prev.map(i => i.label === label ? { ...i, done: !i.done } : i));
  };

  const updateBudget = (label, field, val) => {
    setBudgetItems(prev => prev.map(i => i.label === label ? { ...i, [field]: val } : i));
  };

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ minHeight: '55vh' }}>
        <img src="https://images.unsplash.com/photo-1748551204321-845c277c1dba?w=1400&q=85"
          alt="Anniversaire adulte"
          className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(11,30,32,0.88) 0%, rgba(11,30,32,0.5) 70%, transparent 100%)' }} />
        <div className="relative container-ose flex flex-col justify-end pb-12" style={{ minHeight: '55vh', paddingTop: '5rem' }}>
          <nav className="flex items-center gap-2 text-xs mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
            <Link to="/guides" style={{ color: '#D4AF37' }}>Guides</Link>
            <span>/</span>
            <span style={{ color: '#FFFFFF' }}>Anniversaire adulte</span>
          </nav>
          <span className="inline-block w-fit px-4 py-2 rounded-full text-xs font-bold mb-4"
            style={{ background: 'rgba(212,175,55,0.2)', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.4)' }}>
            🎉 Guide complet 2026
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold mb-3"
            style={{ color: '#FFFFFF', letterSpacing: '-0.03em', maxWidth: '700px', lineHeight: '1.05' }}>
            Organiser un anniversaire adulte parfait
          </h1>
          <p className="text-sm mb-6 max-w-2xl" style={{ color: 'rgba(255,255,255,0.75)', lineHeight: '1.75' }}>
            12 thèmes tendance, checklist interactive 40 points, tableau budget, gestion prestataires et planning Jour J.
            Tout ce qu'il faut pour une soirée inoubliable.
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#checklist"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm"
              style={{ background: '#D4AF37', color: '#0B1E20' }}>
              <Check size={16} /> Voir la checklist
            </a>
            <Link to="/creer-evenement"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm"
              style={{ background: 'rgba(255,255,255,0.12)', color: '#FFFFFF', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)' }}>
              Composer mon événement <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* BARRE PROGRESSION CHECKLIST */}
      <div style={{ background: '#0B1E20', borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
        <div className="container-ose py-3">
          <div className="flex items-center gap-4">
            <p className="text-xs font-semibold" style={{ color: '#D4AF37', whiteSpace: 'nowrap' }}>
              Préparation : {doneCount}/{checkItems.length} ({progress}%)
            </p>
            <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
              <div className="h-full rounded-full transition-all duration-500"
                style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #D4AF37, #5DBCBF)' }} />
            </div>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
              {progress === 100 ? '🎉 Tout est prêt !' : `${100 - progress}% restant`}
            </p>
          </div>
        </div>
      </div>

      {/* ═══ SECTION 1 : THÈMES 2026 ═══════════════════════════════════ */}
      <section className="section-py section-white">
        <div className="container-ose">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1.5 rounded-full text-xs font-bold mb-3"
              style={{ background: 'rgba(244,167,178,0.15)', color: '#B94B5C' }}>✨ Tendances 2026</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              12 thèmes pour votre anniversaire adulte
            </h2>
            <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>Cliquez sur un thème pour l'explorer</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {THEMES.map(t => (
              <button
                key={t.name}
                onClick={() => setActiveTheme(activeTheme === t.name ? null : t.name)}
                className="p-4 rounded-2xl text-left transition-all"
                style={{
                  background: activeTheme === t.name ? '#0B1E20' : '#FFFFFF',
                  border: `1.5px solid ${activeTheme === t.name ? '#D4AF37' : 'var(--border)'}`,
                  cursor: 'pointer',
                }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">{t.emoji}</span>
                  {t.popular && (
                    <span className="px-1.5 py-0.5 rounded text-xs font-bold"
                      style={{ background: 'rgba(212,175,55,0.15)', color: '#C58B19' }}>Populaire</span>
                  )}
                </div>
                <p className="font-bold text-sm mb-1"
                  style={{ color: activeTheme === t.name ? '#FFFFFF' : 'var(--ink)' }}>{t.name}</p>
                {activeTheme === t.name && (
                  <>
                    <p className="text-xs mb-2" style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.5' }}>{t.desc}</p>
                    <div className="flex gap-1">
                      {t.colors.map(c => (
                        <div key={c} className="w-5 h-5 rounded-full border-2 border-white/20" style={{ background: c }} />
                      ))}
                    </div>
                  </>
                )}
                {activeTheme !== t.name && (
                  <p className="text-xs line-clamp-1" style={{ color: 'var(--text-muted)' }}>{t.desc}</p>
                )}
              </button>
            ))}
          </div>

          <div className="mt-8 p-5 rounded-2xl text-center"
            style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)' }}>
            <p className="text-sm font-semibold mb-3" style={{ color: 'var(--ink)' }}>
              💡 Show Événement peut décorer votre thème sur-mesure
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/fabrication"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold"
                style={{ background: '#D4AF37', color: '#0B1E20' }}>
                Arche ballons & déco sur-mesure <ArrowRight size={14} />
              </Link>
              <Link to="/catalogue"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold"
                style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }}>
                Voir tout le catalogue
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 2 : CHECKLIST INTERACTIVE ════════════════════════ */}
      <section id="checklist" className="section-py" style={{ background: 'var(--bg)' }}>
        <div className="container-ose">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1.5 rounded-full text-xs font-bold mb-3"
              style={{ background: 'rgba(93,188,191,0.12)', color: '#0B4B4E' }}>📋 Checklist interactive</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              40 points pour ne rien oublier
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {CHECKLIST_CATEGORIES.map(cat => {
              const Icon = cat.icon;
              const catItems = checkItems.filter(i => i.cat === cat.cat);
              const catDone = catItems.filter(i => i.done).length;
              return (
                <div key={cat.cat} className="card-premium p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${cat.color}18` }}>
                      <Icon size={16} style={{ color: cat.color }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{cat.cat}</p>
                      <p className="text-xs" style={{ color: cat.color }}>{catDone}/{catItems.length} complétés</p>
                    </div>
                    <div className="w-12 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
                      <div className="h-full rounded-full" style={{ width: `${(catDone / catItems.length) * 100}%`, background: cat.color }} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    {cat.items.map(item => {
                      const done = checkItems.find(i => i.label === item.label)?.done;
                      return (
                        <button key={item.label}
                          onClick={() => toggleCheck(item.label)}
                          className="w-full flex items-start gap-3 p-2 rounded-xl text-left transition-all"
                          style={{ background: done ? `${cat.color}0A` : 'transparent', cursor: 'pointer', border: 'none' }}>
                          <div className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ borderColor: done ? cat.color : 'var(--border)', background: done ? cat.color : 'transparent' }}>
                            {done && <Check size={11} color="white" />}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs" style={{ color: done ? 'var(--text-muted)' : 'var(--ink)', textDecoration: done ? 'line-through' : 'none' }}>
                              {item.label}
                            </p>
                            <p className="text-xs" style={{ color: cat.color, opacity: 0.8 }}>⏱ {item.delai}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 p-4 rounded-2xl text-center"
            style={{ background: '#0B1E20' }}>
            <p className="text-sm font-semibold mb-2" style={{ color: '#FFFFFF' }}>
              Sauvegardez votre checklist dans votre espace personnel
            </p>
            <Link to="/connexion"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold"
              style={{ background: '#D4AF37', color: '#0B1E20' }}>
              Créer mon espace gratuit <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 3 : TABLEAU BUDGET ════════════════════════════════ */}
      <section className="section-py section-white">
        <div className="container-ose">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1.5 rounded-full text-xs font-bold mb-3"
              style={{ background: 'rgba(212,175,55,0.12)', color: '#C58B19' }}>💰 Budget estimatif</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              Tableau de suivi budget
            </h2>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Saisissez vos montants pour suivre votre budget en temps réel</p>
          </div>

          {/* Totaux */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { label: 'Budget prévu', value: totalPrevu, color: '#D4AF37' },
              { label: 'Déjà payé', value: totalPaye, color: '#1F9D7A' },
              { label: 'Reste à payer', value: totalRestant, color: totalRestant > 0 ? '#EF4444' : '#1F9D7A' },
            ].map((t, i) => (
              <div key={i} className="p-4 rounded-2xl text-center"
                style={{ background: '#FFFFFF', border: `1px solid ${t.color}30` }}>
                <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>{t.label}</p>
                <p className="font-display text-2xl font-bold" style={{ color: t.color }}>{t.value.toFixed(0)}€</p>
              </div>
            ))}
          </div>

          {/* Tableau */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: '0 4px' }}>
              <thead>
                <tr>
                  {['Poste', 'Catégorie', 'Prévu (€)', 'Réel (€)', 'Payé (€)', 'Restant'].map(h => (
                    <th key={h} className="text-left py-2 px-3 text-xs font-bold" style={{ color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {budgetItems.map((item, idx) => {
                  const restant = (parseFloat(item.prevu) || 0) - (parseFloat(item.paye) || 0);
                  return (
                    <tr key={idx}>
                      <td className="py-2 px-3 rounded-l-xl font-medium text-xs" style={{ background: '#FFFFFF', border: '1px solid var(--border)', borderRight: 0, color: 'var(--ink)' }}>
                        {item.label}
                      </td>
                      <td className="py-2 px-3 text-xs" style={{ background: '#FFFFFF', border: '1px solid var(--border)', borderLeft: 0, borderRight: 0, color: 'var(--text-muted)' }}>
                        {item.cat}
                      </td>
                      {['prevu', 'reel', 'paye'].map(field => (
                        <td key={field} className="py-1 px-2" style={{ background: '#FFFFFF', border: '1px solid var(--border)', borderLeft: 0, borderRight: 0 }}>
                          <input
                            type="number"
                            value={item[field] || ''}
                            onChange={e => updateBudget(item.label, field, e.target.value)}
                            placeholder="0"
                            className="w-full px-2 py-1 rounded-lg text-xs text-right"
                            style={{ border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--ink)', outline: 'none', maxWidth: '80px' }}
                          />
                        </td>
                      ))}
                      <td className="py-2 px-3 rounded-r-xl font-bold text-xs text-right" style={{ background: '#FFFFFF', border: '1px solid var(--border)', borderLeft: 0, color: restant > 0 ? '#EF4444' : '#1F9D7A' }}>
                        {restant > 0 ? `-${restant.toFixed(0)}€` : '✓'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 4 : PRESTATAIRES ══════════════════════════════════ */}
      <section className="section-py" style={{ background: 'var(--bg)' }}>
        <div className="container-ose">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1.5 rounded-full text-xs font-bold mb-3"
              style={{ background: 'rgba(197,139,25,0.12)', color: '#C58B19' }}>🤝 Prestataires</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              Qui contacter pour votre anniversaire ?
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {PRESTATAIRES_TYPES.map((p, i) => (
              <div key={i} className="card-premium p-4"
                style={{ borderLeft: p.urgent ? '3px solid #D4AF37' : '1px solid var(--border)' }}>
                <div className="text-2xl mb-2">{p.icon}</div>
                <p className="font-bold text-sm mb-1" style={{ color: 'var(--ink)' }}>{p.role}</p>
                <p className="text-xs mb-3" style={{ color: 'var(--text-muted)', lineHeight: '1.5' }}>{p.desc}</p>
                {p.urgent && (
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-bold mb-2"
                    style={{ background: 'rgba(212,175,55,0.12)', color: '#C58B19' }}>⚡ Réserver tôt</span>
                )}
                {p.link && (
                  <Link to={p.link}
                    className="flex items-center gap-1 text-xs font-semibold"
                    style={{ color: '#5DBCBF' }}>
                    Voir notre recommandation <ArrowRight size={11} />
                  </Link>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 text-center">
            <Link to="/prestataires-recommandes"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm"
              style={{ background: '#0B1E20', color: '#D4AF37', boxShadow: '0 8px 24px rgba(11,30,32,0.15)' }}>
              Voir tous nos prestataires recommandés <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ SECTION 5 : PLANNING JOUR J ═══════════════════════════════ */}
      <section className="section-py section-white">
        <div className="container-ose max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1.5 rounded-full text-xs font-bold mb-3"
              style={{ background: 'rgba(31,157,122,0.12)', color: '#1F9D7A' }}>📅 Planning</span>
            <h2 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              Planning type Jour J
            </h2>
          </div>
          <div className="space-y-3">
            {[
              { heure: '14h00', action: 'Livraison Show Événement (photobooth, déco, animations)', resp: 'Show Événement', color: '#D4AF37' },
              { heure: '15h00', action: 'Installation et mise en place par l\'équipe', resp: 'Show Événement + vous', color: '#D4AF37' },
              { heure: '17h00', action: 'Arrivée DJ / groupe et test sono', resp: 'DJ', color: '#5DBCBF' },
              { heure: '18h00', action: 'Derniers préparatifs — décoration tables, fleurs', resp: 'Vous', color: '#F4A7B2' },
              { heure: '19h30', action: 'Accueil des invités — cocktail d\'arrivée', resp: 'Vous', color: '#1F9D7A' },
              { heure: '20h00', action: 'Ouverture buffet / repas', resp: 'Traiteur', color: '#C58B19' },
              { heure: '21h00', action: 'Animations, photobooth, discours', resp: 'DJ + vous', color: '#5DBCBF' },
              { heure: '22h00', action: 'Gâteau d\'anniversaire — soufflage bougies', resp: 'Vous', color: '#F4A7B2' },
              { heure: '23h00', action: 'Soirée dansante, photobooth, ambiance', resp: 'DJ', color: '#5DBCBF' },
              { heure: '01h00', action: 'Fin de soirée — début rangement', resp: 'Équipe Show Événement', color: '#D4AF37' },
              { heure: '02h00', action: 'Reprise matériel Show Événement', resp: 'Show Événement', color: '#D4AF37' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-3 rounded-xl"
                style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <div className="w-14 text-center flex-shrink-0">
                  <span className="font-display font-bold text-sm" style={{ color: item.color }}>{item.heure}</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>{item.action}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>👤 {item.resp}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-center mt-4" style={{ color: 'var(--text-muted)' }}>
            ✨ Show Événement peut intervenir de nuit pour la reprise du matériel, sans contrainte pour vous.
          </p>
        </div>
      </section>

      {/* ═══ SECTION 6 : ERREURS À ÉVITER ══════════════════════════════ */}
      <section className="section-py" style={{ background: 'var(--bg)' }}>
        <div className="container-ose">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1.5 rounded-full text-xs font-bold mb-3"
              style={{ background: 'rgba(239,68,68,0.1)', color: '#DC2626' }}>⚠️ Pièges à éviter</span>
            <h2 className="font-display text-2xl font-bold" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
              6 erreurs fréquentes (et comment les éviter)
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ERREURS.map((e, i) => (
              <div key={i} className="card-premium p-5">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={18} style={{ color: '#F59E0B', flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <p className="font-bold text-sm mb-1" style={{ color: 'var(--ink)' }}>{e.erreur}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>{e.conseil}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PRODUITS SHOW ÉVÉNEMENT ════════════════════════════════════ */}
      <section className="section-py" style={{ background: '#0B1E20' }}>
        <div className="container-ose">
          <div className="text-center mb-8">
            <p className="font-display text-2xl font-bold mb-2" style={{ color: '#FFFFFF' }}>
              Show Événement s'occupe de tout
            </p>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Livraison, installation, animation sur place et reprise nocturne — dans un rayon de 1h30 autour de Figeac
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
            {[
              { emoji: '📸', label: 'Photobooth HD', desc: 'Impressions instantanées, fond personnalisé', href: '/produit/p-ph-001' },
              { emoji: '🎈', label: 'Arche ballons', desc: 'Couleurs de votre thème sur-mesure', href: '/fabrication' },
              { emoji: '🍬', label: 'Bar à bonbons', desc: 'Candy bar thématique clé en main', href: '/produit/p-fd-003' },
              { emoji: '🎵', label: 'Sono & éclairage', desc: 'Son et lumière pour l\'ambiance', href: '/catalogue?categorie_id=cat-son-lumiere' },
            ].map((p, i) => (
              <Link key={i} to={p.href}
                className="p-4 rounded-2xl text-center hover:scale-105 transition-transform duration-200"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <div className="text-3xl mb-2">{p.emoji}</div>
                <p className="font-bold text-sm mb-1" style={{ color: '#FFFFFF' }}>{p.label}</p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{p.desc}</p>
              </Link>
            ))}
          </div>
          <div className="text-center">
            <Link to="/creer-evenement"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-base"
              style={{ background: '#D4AF37', color: '#0B1E20', boxShadow: '0 12px 40px rgba(212,175,55,0.35)' }}>
              Composer mon anniversaire adulte <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} sourcePage="Guide anniversaire adulte" />
    </div>
  );
}
